import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { cn, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { notificationsAPI } from '@/lib/api';
import {
  LayoutDashboard,
  Route,
  Truck,
  Briefcase,
  FileText,
  Menu,
  X,
  LogOut,
  User,
  Users,
  CalendarDays,
  ChevronDown,
  Bell,
  AlertTriangle,
  Wrench,
  ShieldAlert,
  MessageSquare,
  CheckCircle,
  DollarSign,
  BookOpen,
  ExternalLink,
  BarChart3,
  Target,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';

// Notification Panel Component
function NotificationPanel({ notifications, unreadCount, onMarkAllRead, onMarkRead, getIcon }) {
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="flex flex-col max-h-96">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <h3 className="font-semibold text-slate-900">Notifications</h3>
        {unreadCount > 0 && (
          <button 
            onClick={onMarkAllRead}
            className="text-xs text-[#52B8E1] hover:text-[#194461] font-medium"
          >
            Mark all read
          </button>
        )}
      </div>
      
      <ScrollArea className="flex-1">
        {notifications.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">
            <Bell size={24} className="mx-auto mb-2 opacity-30" />
            No notifications
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {notifications.map((notif) => (
              <div 
                key={notif.id}
                onClick={() => !notif.is_read && onMarkRead(notif.id)}
                className={cn(
                  "px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors",
                  !notif.is_read && "bg-blue-50/50"
                )}
              >
                <div className="flex gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    {getIcon(notif.type, notif.priority)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={cn(
                        "text-sm text-slate-900 line-clamp-1",
                        !notif.is_read && "font-medium"
                      )}>
                        {notif.title}
                      </p>
                      {!notif.is_read && (
                        <span className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-1.5" />
                      )}
                    </div>
                    <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">
                      {notif.message}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-1">
                      {formatTimeAgo(notif.created_at)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
      
      {notifications.length > 0 && (
        <div className="border-t border-slate-200 p-2">
          <button className="w-full text-center text-xs text-[#52B8E1] hover:text-[#194461] font-medium py-1.5">
            View all notifications
          </button>
        </div>
      )}
    </div>
  );
}

const navItems = [
  { href: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/admin/routes', label: 'Routes', icon: Route },
  { href: '/admin/schedule', label: 'Schedule', icon: CalendarDays },
  { href: '/admin/drivers', label: 'Drivers', icon: Users },
  { href: '/admin/units', label: 'Units', icon: Truck },
  { href: '/admin/jobs', label: 'Jobs', icon: Briefcase },
  { href: '/admin/quote-requests', label: 'Quotes', icon: FileText },
  { href: '/admin/leads', label: 'Leads', icon: Target },
  { href: '/admin/invoices', label: 'Invoices', icon: DollarSign },
  { href: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
];

export default function AdminLayout({ children, title }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isDriver } = useAuth();

  // Fetch notifications
  useEffect(() => {
    if (user && !isDriver) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000); // Poll every 30s
      return () => clearInterval(interval);
    }
  }, [user, isDriver]);

  const fetchUnreadCount = async () => {
    try {
      const response = await notificationsAPI.getUnreadCount();
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await notificationsAPI.getAll({ limit: 10 });
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const handleOpenNotifications = (open) => {
    setNotifOpen(open);
    if (open) {
      fetchNotifications();
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleMarkRead = async (notifId) => {
    try {
      await notificationsAPI.markRead(notifId);
      setNotifications(prev => prev.map(n => 
        n.id === notifId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const getNotificationIcon = (type, priority) => {
    if (priority === 'urgent') return <ShieldAlert size={16} className="text-red-500" />;
    if (type === 'issue_report') return <AlertTriangle size={16} className="text-amber-500" />;
    return <MessageSquare size={16} className="text-blue-500" />;
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  // Check if user is sales role (only sees Leads)
  const isSales = user?.role === 'sales';

  // Sales users only see Leads
  const salesNavItems = [
    { href: '/admin/leads', label: 'Leads', icon: Target },
  ];

  // Driver sees simplified nav
  const displayNavItems = isDriver 
    ? [{ href: `/admin/driver-view/${user?.id}/today`, label: 'My Route', icon: Route }]
    : isSales 
      ? salesNavItems 
      : navItems;

  // Redirect sales users to leads page if they land on dashboard
  useEffect(() => {
    if (isSales && location.pathname === '/admin/dashboard') {
      navigate('/admin/leads');
    }
  }, [isSales, location.pathname, navigate]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-50 flex items-center justify-between px-4">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-md hover:bg-slate-100"
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <div className="flex items-center gap-2">
          <img src="/logo-icon.svg" alt="Tinkle Tent" className="h-8 w-8" />
          <span className="font-bold text-[#194461]">Tinkle Tent</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Mobile Notification Bell */}
          {!isDriver && (
            <Popover open={notifOpen} onOpenChange={handleOpenNotifications}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell-mobile">
                  <Bell size={20} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0">
                <NotificationPanel 
                  notifications={notifications}
                  unreadCount={unreadCount}
                  onMarkAllRead={handleMarkAllRead}
                  onMarkRead={handleMarkRead}
                  getIcon={getNotificationIcon}
                />
              </PopoverContent>
            </Popover>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <User size={20} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled>
                <span className="font-medium">{user?.full_name}</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut size={16} className="mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 bottom-0 w-64 bg-white border-r border-slate-200 z-40 transform transition-transform duration-200 lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center px-6 border-b border-slate-200">
            <Link to="/admin/dashboard" className="flex items-center gap-2">
              <img src="/logo-icon.svg" alt="Tinkle Tent" className="h-10 w-10" />
              <div className="hidden lg:flex flex-col">
                <span className="font-bold text-[#194461] text-lg leading-none">Tinkle Tent</span>
                <span className="text-xs text-slate-500 leading-none -mt-0.5">Rentals</span>
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
            {displayNavItems.map((item) => {
              const isActive = location.pathname === item.href || 
                (item.href !== '/admin/dashboard' && location.pathname.startsWith(item.href));
              const Icon = item.icon;
              
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors",
                    isActive
                      ? "bg-[#194461]/10 text-[#194461] font-medium"
                      : "text-slate-600 hover:bg-slate-100"
                  )}
                >
                  <Icon size={20} />
                  {item.label}
                </Link>
              );
            })}
            
            {/* Documentation Links */}
            {!isDriver && (
              <div className="pt-4 mt-4 border-t border-slate-200">
                <p className="px-3 mb-2 text-xs font-medium text-slate-400 uppercase tracking-wider">Resources</p>
                <a
                  href="/sales-sheet.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100 transition-colors"
                  data-testid="nav-sales-sheet"
                >
                  <FileText size={18} />
                  Sales Sheet
                  <ExternalLink size={12} className="ml-auto opacity-50" />
                </a>
                <a
                  href="/demo-guide.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100 transition-colors"
                  data-testid="nav-demo-guide"
                >
                  <BookOpen size={18} />
                  Demo Guide
                  <ExternalLink size={12} className="ml-auto opacity-50" />
                </a>
              </div>
            )}
          </nav>

          {/* Desktop Notification Bell */}
          {!isDriver && (
            <div className="hidden lg:block px-3 py-2 border-t border-slate-200">
              <Popover open={notifOpen} onOpenChange={handleOpenNotifications}>
                <PopoverTrigger asChild>
                  <button 
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-600"
                    data-testid="notification-bell-desktop"
                  >
                    <div className="relative">
                      <Bell size={20} />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-medium">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </div>
                    <span className="text-sm">Notifications</span>
                    {unreadCount > 0 && (
                      <Badge variant="destructive" className="ml-auto text-[10px] px-1.5 py-0">
                        {unreadCount} new
                      </Badge>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" side="right" className="w-80 p-0 ml-2">
                  <NotificationPanel 
                    notifications={notifications}
                    unreadCount={unreadCount}
                    onMarkAllRead={handleMarkAllRead}
                    onMarkRead={handleMarkRead}
                    getIcon={getNotificationIcon}
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}

          {/* User section */}
          <div className="p-4 border-t border-slate-200">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-[#194461] flex items-center justify-center text-white text-sm font-medium">
                    {user?.full_name?.charAt(0) || 'U'}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-slate-900 truncate">{user?.full_name}</p>
                    <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
                  </div>
                  <ChevronDown size={16} className="text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut size={16} className="mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:pl-64 pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          {title && (
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-6" data-testid="page-title">
              {title}
            </h1>
          )}
          {children}
        </div>
      </main>
    </div>
  );
}
